/* Canonical serialization of the results object R.

   The prototype's R is deliberately cyclic: every topic carries pageData (the Page
   object) and every page carries topics (its topic objects). JSON cannot represent
   cycles, so the stored form strips both links and rebuilds them on revive — the
   client port consumes this exact shape (rebuild pageByUrl as a Map on load).

   Everything else is byte-identical to the in-memory R. */

export function canonicalize(R) {
  const stripTopic = t => { const { pageData, ...copy } = t; return copy; };
  const stripPage = p => { const { topics, ...copy } = p; return copy; };
  return {
    ...R,
    topics: R.topics.map(stripTopic),
    opportunities: R.opportunities.map(stripTopic),
    pages: R.pages.map(stripPage),
    pageByUrl: Object.fromEntries([...R.pageByUrl.entries()].map(([u, p]) => [u, stripPage(p)])),
  };
}

export function revive(canonical) {
  const R = { ...canonical };
  /* Rebuild pageByUrl from the pages array so both point at the SAME objects, the way
     the in-memory R does. Rebuilding it from the serialized pageByUrl instead creates a
     second set of page copies, and any view that looks a page up by URL then sees a page
     with no .topics (crashed every page detail before 2026-08-24). */
  R.pages = canonical.pages.map(p => ({ ...p }));
  R.pageByUrl = new Map(R.pages.map(p => [p.url, p]));
  for (const [url, p] of Object.entries(canonical.pageByUrl)) {
    if (!R.pageByUrl.has(url)) { const copy = { ...p }; R.pages.push(copy); R.pageByUrl.set(url, copy); }
  }
  for (const p of R.pages) p.topics = R.topics.filter(x => x.page === p.url);
  for (const t of R.topics) t.pageData = t.page ? (R.pageByUrl.get(t.page) || null) : null;
  /* Opportunities are the same topic objects in memory; restore that identity so the
     opportunity list and the topic detail always agree (AI extras, absorbed queries). */
  const byId = new Map(R.topics.map(t => [t.id, t]));
  R.opportunities = canonical.opportunities.map(o => byId.get(o.id) || o);
  return R;
}